import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import type VueRouter from 'vue-router';
import type { RawLocation } from 'vue-router';

// Navigating to the location we're already on is a no-op, not a failure — but Vue Router still
// rejects it, and that rejection is only observable on the promise the navigation returns
// (router.onError deliberately filters navigation failures out before its callbacks run). So
// every caller that can legitimately re-navigate to its current route has to opt into tolerating
// it, and an unguarded call surfaces as an unhandled NavigationDuplicated in the console.
//
// These helpers keep that opt-in explicit at the call site while stating the policy — which
// failures are benign — once. Prefer them over a blanket `.catch(() => {})`, which also eats
// genuine routing errors.

/** True when the error is Vue Router rejecting a navigation to the current location. */
export function isDuplicateNavigation(error: unknown): boolean {
    // Coerced: vue-router's own check short-circuits on the absent `_isRouter` property, so it
    // hands back undefined rather than false for anything that isn't one of its failures.
    return !!isNavigationFailure(error, NavigationFailureType.duplicated);
}

/**
 * Replaces the current route, treating a redundant navigation to the location we're already on
 * as success. Any other failure — including other navigation-failure types — is rethrown so
 * genuine routing errors still reach the caller.
 */
export async function replaceIgnoringDuplicate(router: VueRouter, location: RawLocation): Promise<void> {
    try {
        await router.replace(location);
    } catch (navigationError) {
        if (!isDuplicateNavigation(navigationError)) {
            throw navigationError;
        }
    }
}

/** As {@link replaceIgnoringDuplicate}, but adding a history entry rather than replacing one. */
export async function pushIgnoringDuplicate(router: VueRouter, location: RawLocation): Promise<void> {
    try {
        await router.push(location);
    } catch (navigationError) {
        if (!isDuplicateNavigation(navigationError)) {
            throw navigationError;
        }
    }
}
